import { Component, EventEmitter, Input, OnInit, Output, inject } from "@angular/core";
import { firstValueFrom, of } from "rxjs";
import { AiSettingsService } from "@app/ai/services/ai-settings-data.service";
import { CerrixPromptService } from "@app/shared/services/cerrix-prompt.service";
import { GenericListFieldType } from "@app/shared/models/GenericList/GenericListField";
import { TranslatePipe, injectTranslation, provideTranslationScope } from "@app/shared/i18n/translation-scope";
import { scope } from "@app/controls/i18n/scope";
import { AdvEffDataService } from "../../../shared/services/adv-eff-data.service";
import { EffExpansionPanelComponent } from "../../common/eff-expansion-panel/eff-expansion-panel.component";
import { EffButtonComponent } from "../../common/eff-button/eff-button.component";
import { EffIconComponent } from "../../common/eff-icon/eff-icon.component";
import {
    EffAiReviewCommentsModel,
    EffAiReviewFeedbackReason,
    EffAiReviewItemType,
    EffAiReviewModel,
} from "./eff-ai-review.model";

@Component({
    selector: "app-eff-ai-review-info-box",
    templateUrl: "./eff-ai-review-info-box.component.html",
    styleUrls: ["./eff-ai-review-info-box.component.scss"],
    imports: [TranslatePipe, EffExpansionPanelComponent, EffButtonComponent, EffIconComponent],
    viewProviders: [provideTranslationScope(scope)],
})
export class EffAiReviewInfoBoxComponent implements OnInit {
    private _aiSettingsService = inject(AiSettingsService);
    private _ds = inject(AdvEffDataService);
    private _promptService = inject(CerrixPromptService);
    private _translation = injectTranslation(scope);

    @Input() effectivenessGuid: string;
    // The proposed reviewer comments of the selected items. The page puts them in the comment fields; the
    // reviewer saves them.
    @Output() commentsProposed = new EventEmitter<EffAiReviewCommentsModel>();

    aiEnabled = false;
    loading = false;
    writingComments = false;
    review: EffAiReviewModel;
    protected readonly itemType = EffAiReviewItemType;
    // The relevance the user gave per item, keyed by itemType-itemIndex.
    feedback = new Map<string, boolean>();
    // The items the user selected to turn into reviewer comments, keyed by itemType-itemIndex. Apart from the
    // feedback, so selecting an item does not rate it.
    selected = new Set<string>();

    async ngOnInit() {
        this.aiEnabled = await firstValueFrom(this._aiSettingsService.isEnabled());
    }

    async getReview() {
        this.loading = true;
        try {
            this.review = await firstValueFrom(this._ds.getAiReview(this.effectivenessGuid));
            this.feedback.clear();
            this.selected.clear();
        } finally {
            this.loading = false;
        }
    }

    isSelected(itemType: EffAiReviewItemType, itemIndex: number): boolean {
        return this.selected.has(`${itemType}-${itemIndex}`);
    }

    toggleSelected(itemType: EffAiReviewItemType, itemIndex: number) {
        const key = `${itemType}-${itemIndex}`;
        if (!this.selected.delete(key)) {
            this.selected.add(key);
        }
    }

    async writeComments() {
        this.writingComments = true;
        try {
            const comments = await firstValueFrom(
                this._ds.postAiReviewComments(this.effectivenessGuid, {
                    reviewId: this.review.reviewId,
                    findings: this.review.findings.filter((_, i) => this.isSelected(EffAiReviewItemType.Finding, i)),
                    improvements: this.review.improvements.filter((_, i) =>
                        this.isSelected(EffAiReviewItemType.Improvement, i)
                    ),
                })
            );
            this.commentsProposed.emit(comments);
        } finally {
            this.writingComments = false;
        }
    }

    getFeedback(itemType: EffAiReviewItemType, itemIndex: number): boolean | undefined {
        return this.feedback.get(`${itemType}-${itemIndex}`);
    }

    async giveFeedback(itemType: EffAiReviewItemType, itemIndex: number, itemText: string, relevant: boolean) {
        let reasons: EffAiReviewFeedbackReason[] = [];
        let comment: string = null;

        if (!relevant) {
            const result = await this.askWhatCouldBeImproved();
            if (!result) {
                return;
            }

            reasons = result.reasons;
            comment = reasons.includes(EffAiReviewFeedbackReason.Other) ? result.comment : null;
        }

        await firstValueFrom(
            this._ds.postAiReviewFeedback(this.effectivenessGuid, {
                reviewId: this.review.reviewId,
                itemType,
                itemIndex,
                itemText,
                relevant,
                reasons,
                comment,
            })
        );
        this.feedback.set(`${itemType}-${itemIndex}`, relevant);
    }

    private async askWhatCouldBeImproved(): Promise<{ reasons: EffAiReviewFeedbackReason[]; comment?: string } | false> {
        await this._translation.ready();
        const reasonOptions = Object.values(EffAiReviewFeedbackReason)
            .filter((reason): reason is EffAiReviewFeedbackReason => typeof reason === "number")
            .map((reason) => ({
                ID: reason,
                Name: this._translation.t(`effAiReviewInfoBox.feedbackReason${EffAiReviewFeedbackReason[reason]}`),
            }));
        const fieldResult = { reasons: <EffAiReviewFeedbackReason[]>[] };

        return this._promptService
            .confirmCustom({
                maxWidth: "500px",
                maxHeight: "500px",
                data: {
                    title: this._translation.t("effAiReviewInfoBox.feedbackTitleNotRelevant"),
                    message: this._translation.t("effAiReviewInfoBox.feedbackMessage"),
                    fieldResult,
                    fields: [
                        {
                            prettyName: this._translation.t("effAiReviewInfoBox.feedbackReasons"),
                            fieldName: "reasons",
                            fieldType: GenericListFieldType.MultiSelect,
                            getDataMethod: () => of(reasonOptions),
                            // The prompt does not load the options of a field itself.
                            selfInit: true,
                        },
                        {
                            prettyName: this._translation.t("effAiReviewInfoBox.feedbackOther"),
                            fieldName: "comment",
                            fieldType: GenericListFieldType.TextArea,
                            // A getter, because the prompt reads it again after each change of the reasons.
                            get hideInEdit() {
                                return !fieldResult.reasons?.includes(EffAiReviewFeedbackReason.Other);
                            },
                        },
                    ],
                    confirmButton: {
                        text: this._translation.t("effAiReviewInfoBox.feedbackSend"),
                    },
                },
            })
            .toPromise();
    }
}
